import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import { PRODUCTION_URL } from '../productionLink/productionLink';

const StoresSetup = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeTab, setActiveTab] = useState('items');
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444'
  };

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([fetchItems(), fetchSuppliers()]);
    setLoading(false);
  };

  const fetchItems = async () => {
    try {
      const data = await apiGet('/admin/stores/setup/items');
      if (data.success) setItems(data.data);
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const data = await apiGet('/admin/stores/setup/suppliers');
      if (data.success) setSuppliers(data.data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const endpoint = modalType === 'item' ? 'items' : 'suppliers';

    try {
      const url = editingItem
        ? `${PRODUCTION_URL}/api/admin/stores/setup/${endpoint}/${editingItem.id}`
        : `${PRODUCTION_URL}/api/admin/stores/setup/${endpoint}`;

      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMessage(`✅ ${modalType === 'item' ? 'Item' : 'Supplier'} ${editingItem ? 'updated' : 'created'} successfully!`);
        setShowModal(false);
        setEditingItem(null);
        setFormData({});
        fetchAllData();
      }
    } catch (error) {
      console.error('Error saving:', error);
    }
  };

  const handleEdit = (type, item) => {
    setModalType(type);
    setEditingItem(item);
    setFormData(item);
    setShowModal(true);
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    const endpoint = type === 'item' ? 'items' : 'suppliers';

    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/stores/setup/${endpoint}/${id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        setSuccessMessage('✅ Item deleted successfully!');
        fetchAllData();
      }
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const openModal = (type) => {
    setModalType(type);
    setEditingItem(null);
    setFormData(getDefaultFormData(type));
    setShowModal(true);
  };

  const getDefaultFormData = (type) => {
    if (type === 'item') {
      return {
        item_name: '',
        item_code: '',
        category: 'food',
        unit: '',
        current_stock: 0,
        minimum_stock: 0,
        maximum_stock: 0,
        unit_price: 0,
        reorder_point: 0,
        location: '',
        is_active: true
      };
    } else {
      return {
        supplier_name: '',
        contact_person: '',
        phone: '',
        email: '',
        address: '',
        supplier_type: 'general',
        payment_terms: '',
        is_active: true
      };
    }
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toLocaleString('en-IN')}`;
  };

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '16px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    pageTitle: {
      fontSize: isMobile ? '20px' : isTablet ? '24px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px'
    },
    tabContainer: {
      display: 'flex',
      gap: '8px',
      marginBottom: isMobile ? '16px' : '20px',
      padding: isMobile ? '0 4px' : '0',
      overflowX: 'auto'
    },
    tab: {
      padding: isMobile ? '10px 16px' : '12px 20px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      background: colors.white,
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      whiteSpace: 'nowrap'
    },
    activeTab: {
      background: colors.primary,
      color: colors.white,
      borderColor: colors.primary
    },
    contentCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 4px' : '0'
    },
    sectionTitle: {
      fontSize: isMobile ? '16px' : '20px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: isMobile ? '12px' : '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      justifyContent: 'space-between'
    },
    button: {
      padding: isMobile ? '8px 14px' : '10px 18px',
      borderRadius: '6px',
      border: 'none',
      fontSize: isMobile ? '12px' : '13px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: colors.primary,
      color: colors.white
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: isMobile ? '12px' : '13px'
    },
    tableHeader: {
      background: colors.lightGray,
      fontWeight: '700',
      textAlign: 'left',
      padding: isMobile ? '10px' : '12px',
      borderBottom: `2px solid ${colors.mediumGray}`
    },
    tableCell: {
      padding: isMobile ? '10px' : '12px',
      borderBottom: `1px solid ${colors.lightGray}`
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '20px' : '40px'
    },
    modalContent: {
      background: colors.white,
      borderRadius: isMobile ? '16px' : '20px',
      padding: isMobile ? '20px' : '32px',
      maxWidth: isMobile ? '100%' : '600px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto',
      boxSizing: 'border-box'
    },
    formGroup: {
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      marginBottom: '6px',
      fontSize: '12px',
      fontWeight: '600',
      color: colors.textDark
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: '13px',
      fontFamily: 'inherit',
      boxSizing: 'border-box'
    },
    select: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: '13px',
      fontFamily: 'inherit',
      background: colors.white,
      boxSizing: 'border-box'
    },
    actionButton: {
      padding: '6px 12px',
      borderRadius: '6px',
      border: 'none',
      fontSize: '11px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginRight: '6px'
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading setup...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {successMessage && (
        <div style={{
          padding: isMobile ? '12px 16px' : '14px 20px',
          borderRadius: '10px',
          marginBottom: '16px',
          fontSize: isMobile ? '13px' : '14px',
          fontWeight: '600',
          background: 'rgba(74, 124, 42, 0.15)',
          color: colors.success,
          border: `2px solid ${colors.success}`,
          margin: isMobile ? '0 4px 12px 4px' : '0 0 16px 0'
        }}>
          {successMessage}
        </div>
      )}

      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Stores Setup</h1>
        <p style={{ fontSize: isMobile ? '12px' : '14px', color: colors.textLight }}>
          {isMobile ? 'Items and suppliers' : 'Configure inventory items, suppliers, and procurement settings'}
        </p>
      </div>

      {/* Tabs */}
      <div style={styles.tabContainer}>
        <button
          onClick={() => setActiveTab('items')}
          style={{ ...styles.tab, ...(activeTab === 'items' ? styles.activeTab : {}) }}
        >
          📦 Inventory Items
        </button>
        <button
          onClick={() => setActiveTab('suppliers')}
          style={{ ...styles.tab, ...(activeTab === 'suppliers' ? styles.activeTab : {}) }}
        >
          🏢 Suppliers
        </button>
      </div>

      {/* Inventory Items Tab */}
      {activeTab === 'items' && (
        <div style={styles.contentCard}>
          <div style={styles.sectionTitle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>📦</span>
              <span>Inventory Items</span>
            </div>
            <button onClick={() => openModal('item')} style={styles.button}>
              + Add Item
            </button>
          </div>

          {items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
              No items found
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.tableHeader}>Item Code</th>
                    <th style={styles.tableHeader}>Item Name</th>
                    <th style={styles.tableHeader}>Category</th>
                    {!isMobile && <th style={styles.tableHeader}>Stock</th>}
                    <th style={styles.tableHeader}>Unit</th>
                    {!isMobile && <th style={styles.tableHeader}>Price</th>}
                    <th style={styles.tableHeader}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr key={item.id}>
                      <td style={styles.tableCell}><strong>{item.item_code}</strong></td>
                      <td style={styles.tableCell}>{item.item_name}</td>
                      <td style={{ ...styles.tableCell, textTransform: 'capitalize' }}>
                        {item.category}
                      </td>
                      {!isMobile && (
                        <td style={styles.tableCell}>{parseFloat(item.current_stock).toFixed(2)}</td>
                      )}
                      <td style={styles.tableCell}>{item.unit}</td>
                      {!isMobile && (
                        <td style={styles.tableCell}>
                          <strong>{formatCurrency(item.unit_price)}</strong>
                        </td>
                      )}
                      <td style={styles.tableCell}>
                        <button
                          onClick={() => handleEdit('item', item)}
                          style={{ ...styles.actionButton, background: colors.accent, color: colors.white }}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => handleDelete('item', item.id)}
                          style={{ ...styles.actionButton, background: colors.danger, color: colors.white }}
                        >
                          🗑️ Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Suppliers Tab */}
      {activeTab === 'suppliers' && (
        <div style={styles.contentCard}>
          <div style={styles.sectionTitle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>🏢</span>
              <span>Suppliers</span>
            </div>
            <button onClick={() => openModal('supplier')} style={styles.button}>
              + Add Supplier
            </button>
          </div>

          {suppliers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
              No suppliers found
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: '16px' }}>
              {suppliers.map(supplier => (
                <div
                  key={supplier.id}
                  style={{
                    padding: isMobile ? '16px' : '20px',
                    background: colors.lightGray,
                    borderRadius: '12px',
                    border: `1px solid ${colors.mediumGray}`
                  }}
                >
                  <div style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '700', marginBottom: '8px', color: colors.primary }}>
                    {supplier.supplier_name}
                  </div>
                  <div style={{ fontSize: '13px', marginBottom: '6px' }}>
                    <strong>Contact:</strong> {supplier.contact_person}
                  </div>
                  <div style={{ fontSize: '12px', color: colors.textLight, marginBottom: '6px' }}>
                    📞 {supplier.phone}
                  </div>
                  <div style={{ fontSize: '12px', color: colors.textLight, marginBottom: '12px' }}>
                    📧 {supplier.email}
                  </div>
                  <div style={{ fontSize: '11px', color: colors.textLight, marginBottom: '12px', textTransform: 'capitalize' }}>
                    Type: {supplier.supplier_type}
                  </div>
                  <div style={{ fontSize: '11px', color: colors.textLight, marginBottom: '12px' }}>
                    Total Orders: {supplier.total_orders || 0} • {formatCurrency(supplier.total_purchased)}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleEdit('supplier', supplier)}
                      style={{ ...styles.actionButton, background: colors.accent, color: colors.white, flex: 1 }}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDelete('supplier', supplier.id)}
                      style={{ ...styles.actionButton, background: colors.danger, color: colors.white, flex: 1 }}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Universal Modal */}
      {showModal && (
        <div style={styles.modal} onClick={() => setShowModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: isMobile ? '18px' : '22px', marginBottom: '20px', fontWeight: '700' }}>
              {editingItem ? `Edit ${modalType === 'item' ? 'Item' : 'Supplier'}` : `Add New ${modalType === 'item' ? 'Item' : 'Supplier'}`}
            </h3>

            <form onSubmit={handleSubmit}>
              {/* Item Form */}
              {modalType === 'item' && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Item Name *</label>
                      <input
                        type="text"
                        value={formData.item_name || ''}
                        onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
                        style={styles.input}
                        required
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Item Code *</label>
                      <input
                        type="text"
                        value={formData.item_code || ''}
                        onChange={(e) => setFormData({ ...formData, item_code: e.target.value })}
                        style={styles.input}
                        required
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Category *</label>
                      <select
                        value={formData.category || 'food'}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        style={styles.select}
                        required
                      >
                        <option value="food">Food</option>
                        <option value="beverage">Beverage</option>
                        <option value="cleaning">Cleaning</option>
                        <option value="stationery">Stationery</option>
                        <option value="linen">Linen</option>
                        <option value="equipment">Equipment</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Unit *</label>
                      <input
                        type="text"
                        value={formData.unit || ''}
                        onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                        style={styles.input}
                        placeholder="e.g., kg, liters, pieces"
                        required
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '12px' }}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Current Stock</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.current_stock || 0}
                        onChange={(e) => setFormData({ ...formData, current_stock: e.target.value })}
                        style={styles.input}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Min Stock</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.minimum_stock || 0}
                        onChange={(e) => setFormData({ ...formData, minimum_stock: e.target.value })}
                        style={styles.input}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Max Stock</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.maximum_stock || 0}
                        onChange={(e) => setFormData({ ...formData, maximum_stock: e.target.value })}
                        style={styles.input}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Unit Price (₹)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.unit_price || 0}
                        onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                        style={styles.input}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Reorder Point</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.reorder_point || 0}
                        onChange={(e) => setFormData({ ...formData, reorder_point: e.target.value })}
                        style={styles.input}
                      />
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Location</label>
                    <input
                      type="text"
                      value={formData.location || ''}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      style={styles.input}
                      placeholder="e.g., Store Room A"
                    />
                  </div>
                </>
              )}

              {/* Supplier Form */}
              {modalType === 'supplier' && (
                <>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Supplier Name *</label>
                    <input
                      type="text"
                      value={formData.supplier_name || ''}
                      onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                      style={styles.input}
                      required
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Contact Person</label>
                      <input
                        type="text"
                        value={formData.contact_person || ''}
                        onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                        style={styles.input}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Phone *</label>
                      <input
                        type="tel"
                        value={formData.phone || ''}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        style={styles.input}
                        required
                      />
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Email</label>
                    <input
                      type="email"
                      value={formData.email || ''}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      style={styles.input}
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Address</label>
                    <textarea
                      value={formData.address || ''}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      style={{ ...styles.input, minHeight: '80px' }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Supplier Type</label>
                      <select
                        value={formData.supplier_type || 'general'}
                        onChange={(e) => setFormData({ ...formData, supplier_type: e.target.value })}
                        style={styles.select}
                      >
                        <option value="food">Food</option>
                        <option value="beverage">Beverage</option>
                        <option value="equipment">Equipment</option>
                        <option value="general">General</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Payment Terms</label>
                      <input
                        type="text"
                        value={formData.payment_terms || ''}
                        onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                        style={styles.input}
                        placeholder="e.g., 30 days"
                      />
                    </div>
                  </div>
                </>
              )}

              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button type="submit" style={{ ...styles.button, flex: 1 }}>
                  {editingItem ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{ ...styles.button, background: colors.mediumGray, color: colors.textDark, flex: 1 }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default StoresSetup;